import { useCallback, useMemo, useState } from 'react';
import {
  myDay,
  myDayAddItem,
  myDayDecide,
  myDayEvent,
  myDayIndependence,
  myDayIntent,
  type ApiError,
  type MyDayCategory,
  type MyDayEventBody,
  type MyDayItem,
  type MyDayResponse,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { useReadOnly } from '../../ui/ReadOnly';

// My day, shared by Aithora Home (Today, member) and Atlas (Resident "My day").
// A port of the V4 design's mdLayer (L36750) and mdIntent (L36850): the same rows, tags,
// progress and styles, but every value comes from the real routes in
// src/aithora/v3/worklist/my-day.ts, and every tap is a write to them (never local
// storage). Atlas's plain-words path is POST /my-day/intent, which parses on the
// server and records exactly the same event a tap would.

export interface MyDayItemView {
  id: string;
  time: string;
  label: string;
  tag: string;
  why: string;
  /** The design's CSS strings, rendered with sx(). */
  tagStyle: string;
  rowStyle: string;
  isWater: boolean;
  canDo: boolean;
  isDone: boolean;
  doneText: string;
  /** One style per glass (the design draws six). */
  glasses: string[];
  waterText: string;
  onGlass(): void;
  onDo(): void;
  onHelp(): void;
  onUndo(): void;
}

export interface MyDayState {
  loading: boolean;
  error: ApiError | null;
  /** The whole day, in order. */
  items: MyDayItemView[];
  /** What the screen shows: the next MY_DAY_SHOWN open items, or the whole day once opened. */
  shown: MyDayItemView[];
  /** "Later today · 7 more · 1 done  →" / "Show less"; null when everything fits. */
  moreLabel: string | null;
  toggleMore(): void;
  progress: string;
  pct: string;
  streak: string | null;
  suggestions: { id: string; from: string; what: string; onYes(): void; onNo(): void }[];
  draft: string;
  setDraft(v: string): void;
  add(): void;
  privacy: string;
  intent(text: string): Promise<string | null>;
}

/** Six glasses a day (the design's GOAL and its "Six glasses today" line). */
export const WATER_GOAL = 6;

export const MY_DAY_PRIVACY =
  'This is your list. If you miss your pills or blood pressure, your care team is told so they can help. Everything else stays between you and Atlas unless you choose to share it.';

/** The design's reply when Atlas did not understand (mdLayer ask()). */
export const MY_DAY_NOT_CAUGHT = 'I did not catch that. Try “I took my pills”, “I had a glass of water” or “remind me to call Marcus”.';

const TAG: Record<MyDayCategory, { name: string; fg: string; bg: string }> = {
  MEALS: { name: 'Meals', fg: '#6B4A12', bg: 'rgba(184,134,11,.12)' },
  REST: { name: 'Rest', fg: '#3C454F', bg: 'rgba(34,39,46,.08)' },
  HEALTH: { name: 'Health', fg: '#12503C', bg: 'rgba(31,107,82,.12)' },
  MOVING: { name: 'Moving', fg: '#264C7E', bg: 'rgba(47,92,151,.12)' },
  PEOPLE: { name: 'People', fg: '#6B3A2E', bg: 'rgba(107,58,46,.10)' },
  WATER: { name: 'Water', fg: '#264C7E', bg: 'rgba(47,92,151,.12)' },
  ME: { name: 'Me', fg: '#3C454F', bg: 'rgba(34,39,46,.08)' },
};

export const tagStyle = (c: MyDayCategory) => {
  const t = TAG[c] ?? TAG.ME;
  return 'font-size:16px;font-weight:700;letter-spacing:.04em;border-radius:6px;padding:2px 8px;white-space:nowrap;color:' + t.fg + ';background:' + t.bg + ';';
};
export const rowStyle = (done: boolean) =>
  'display:flex;align-items:center;gap:10px 16px;flex-wrap:wrap;min-width:0;min-height:72px;padding:12px 4px;border-top:1px solid rgba(34,39,46,.08);transition:background .3s,opacity .3s;' +
  (done ? 'background:rgba(31,107,82,.06);' : '');
export const glassStyle = (full: boolean) =>
  'display:inline-block;width:12px;height:20px;border-radius:0 0 4px 4px;border:1.5px solid #2F5C97;box-sizing:border-box;' + (full ? 'background:#2F5C97;' : 'background:transparent;');

/** "14:00" -> "2:00 pm", as the V4 design writes its times (mdLayer base list, L36762). */
export function dayClock(hhmm: string): string {
  const h = Number(hhmm.slice(0, 2));
  return `${h % 12 || 12}:${hhmm.slice(3, 5)} ${h < 12 ? 'am' : 'pm'}`;
}

/** The older-adult screen shows what is next, not the whole day: this many open items (V4 mdLayer). */
export const MY_DAY_SHOWN = 4;

/** A done time as the design prints it ("9:12 AM"). */
function doneClock(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Where an item came from, in the person's words (the design's "why" line). */
function whyOf(i: MyDayItem): string {
  if (i.category === 'WATER') return 'Six glasses today keeps you steady on your feet';
  if (i.source === 'SELF') return 'You added this';
  if (i.source === 'FAMILY') return 'Your family asked, you said yes';
  if (i.source === 'FAMILY_DATE') return 'From your family dates';
  if (i.source === 'SCHEDULE') return i.category === 'MEALS' ? 'From the dining schedule' : 'From the building’s schedule';
  return i.isSafety ? 'From your care plan · your care team is told if it is missed' : 'From your care plan';
}

/** The design's order: water (all day) first, then by time, then anything untimed. */
function sortKey(i: MyDayItem): number {
  if (i.category === 'WATER' && !i.dueTime) return -1;
  if (!i.dueTime) return 9999;
  return Number(i.dueTime.slice(0, 2)) * 60 + Number(i.dueTime.slice(3, 5));
}

const firstName = (n: string | null) => (n ? n.trim().split(/\s+/)[0] : 'Your family');

export function useMyDay(opts: { personId: string | null; variant: 'resident' | 'member'; readOnly?: boolean }): MyDayState {
  const { client, session } = useSession();
  const roleReadOnly = useReadOnly();
  const readOnly = !!opts.readOnly || roleReadOnly;
  const personId = opts.personId ?? undefined;
  const q = useQuery<MyDayResponse>((signal) => myDay(client, { query: { personId }, signal }), [client, session?.token, personId]);
  const ind = useQuery((signal) => myDayIndependence(client, { query: { personId, weeks: 1 }, signal }), [client, session?.token, personId]);
  const [draft, setDraft] = useState('');
  const [allOn, setAllOn] = useState(false);
  const { reload } = q;

  const send = useCallback(
    async (body: Omit<MyDayEventBody, 'personId'>) => {
      if (readOnly) return;
      try {
        await myDayEvent(client, { body: { ...body, personId } });
      } finally {
        reload();
        ind.reload();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, personId, readOnly, reload],
  );

  const decide = useCallback(
    async (id: string, accept: boolean) => {
      if (readOnly) return;
      try {
        await myDayDecide(client, { params: { id }, body: { accept } });
      } finally {
        reload();
      }
    },
    [client, readOnly, reload],
  );

  const add = useCallback(() => {
    const label = draft.trim();
    if (!label || readOnly) return;
    setDraft('');
    myDayAddItem(client, { body: { personId, label, category: 'ME' } })
      .catch(() => setDraft(label))
      .finally(reload);
  }, [client, draft, personId, readOnly, reload]);

  const intent = useCallback(
    async (text: string): Promise<string | null> => {
      const utterance = text.trim();
      if (!utterance || readOnly) return null;
      try {
        const r = await myDayIntent(client, { body: { personId, utterance } });
        return r.reply || MY_DAY_NOT_CAUGHT;
      } catch {
        return null;
      } finally {
        reload();
        ind.reload();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, personId, readOnly, reload],
  );

  const d = q.data;
  const derived = useMemo(() => {
    if (!d) return { items: [] as MyDayItemView[], progress: '', pct: '0%', suggestions: [] as MyDayState['suggestions'] };
    const glasses = d.waterGlasses;
    const list = d.items.slice().sort((a, b) => sortKey(a) - sortKey(b));
    const waterDone = glasses >= WATER_GOAL;
    const items: MyDayItemView[] = list.map((i) => {
      const isWater = i.category === 'WATER';
      const done = i.state === 'done' || i.state === 'done_with_help';
      const help = i.state === 'done_with_help';
      const wDone = isWater && waterDone;
      return {
        id: i.id,
        time: i.dueTime ? dayClock(i.dueTime) : isWater ? 'All day' : 'Today',
        label: i.label,
        tag: (TAG[i.category] ?? TAG.ME).name,
        why: i.note ?? whyOf(i),
        tagStyle: tagStyle(i.category),
        rowStyle: rowStyle(done || wDone),
        isWater,
        canDo: !isWater && !done,
        isDone: (!isWater && done) || wDone,
        doneText: wDone ? 'Six glasses. Well done' : done ? (help ? 'Done with help ' : 'Done ') + doneClock(i.doneAt) + (help ? '' : ' · nice work') : '',
        glasses: Array.from({ length: WATER_GOAL }, (_, k) => glassStyle(k < glasses)),
        waterText: `${Math.min(glasses, WATER_GOAL)} of ${WATER_GOAL} glasses`,
        onGlass: () => void send({ itemId: i.id, action: 'WATER_GLASS', glasses: 1 }),
        onDo: () => void send({ itemId: i.id, action: 'DONE' }),
        onHelp: () => void send({ itemId: i.id, action: 'DONE_WITH_HELP' }),
        // A water glass undoes the last glass; anything else undoes its own last tap.
        onUndo: () => void send(isWater ? { action: 'UNDO' } : { itemId: i.id, action: 'UNDO' }),
      };
    });
    const doneN = items.filter((v) => v.isDone).length;
    return {
      items,
      progress: items.length ? `${doneN} of ${items.length} done` : '',
      pct: `${Math.round((100 * doneN) / Math.max(1, items.length))}%`,
      suggestions: d.suggestions.map((s) => ({
        id: s.id,
        from: firstName(s.from),
        what: s.label + (s.dueTime ? ` at ${dayClock(s.dueTime)}` : ''),
        onYes: () => void decide(s.id, true),
        onNo: () => void decide(s.id, false),
      })),
    };
  }, [d, send, decide]);

  // The server's own streak line (PROPOSED), else this week's count from
  // GET /my-day/independence; nothing done yet says nothing.
  const week = ind.data?.weeks[ind.data.weeks.length - 1];
  const counted = week && week.done + week.doneWithHelp > 0
    ? `${week.done + week.doneWithHelp} done this week${week.doneWithHelp ? `, ${week.doneWithHelp} with help` : ''}`
    : null;
  const streak = d?.streak ?? counted;

  // V4: up to MY_DAY_SHOWN open items in time order; done items fold away until "Later today".
  const open = derived.items.filter((v) => !v.isDone);
  const doneN = derived.items.length - open.length;
  const shown = allOn ? derived.items : open.slice(0, MY_DAY_SHOWN);
  const hidden = derived.items.length - shown.length;
  const moreLabel = allOn ? 'Show less' : hidden > 0 ? `Later today · ${open.length - shown.length} more${doneN ? ` · ${doneN} done` : ''}  →` : null;

  return {
    loading: q.loading && !d,
    error: q.error,
    items: derived.items,
    shown,
    moreLabel,
    toggleMore: () => setAllOn((v) => !v),
    progress: derived.progress,
    pct: derived.pct,
    streak,
    suggestions: derived.suggestions,
    draft,
    setDraft,
    add,
    privacy: MY_DAY_PRIVACY,
    intent,
  };
}
